import type { Tenant } from '../lib/database.types';
import { hoursArrayToMap, displayHours } from '../lib/slots';
import Socials from './Socials';

/**
 * Section « Le restaurant » (id=infos) — portée À L'IDENTIQUE du legacy.
 * 2 colonnes : à gauche présentation + adresse + téléphone + réseaux + horaires + CTA ;
 * à droite la carte Google Maps + lien itinéraire. Tout piloté par le tenant
 * (M14 : horaires dérivés de `opening_hours`, même source que les créneaux ; réseaux vides masqués).
 */
interface StoreInfoProps {
  tenant: Tenant;
  onOrder: () => void;
}

function InfoLine({
  icon,
  label,
  value,
  link,
}: {
  icon: string;
  label: string;
  value: string;
  link?: string;
}) {
  const inner = (
    <div className="flex gap-3">
      <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-gray-100">
        {icon}
      </div>
      <div>
        <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</div>
        <div className="font-bold text-gray-900">{value}</div>
      </div>
    </div>
  );
  return link ? (
    <a href={link} className="block transition hover:opacity-70">
      {inner}
    </a>
  ) : (
    inner
  );
}

export default function StoreInfo({ tenant, onOrder }: StoreInfoProps) {
  const fullAddress = [tenant.address_line, [tenant.postal_code, tenant.city].filter(Boolean).join(' ')]
    .filter((p) => p && p.trim().length > 0)
    .join(', ');
  const rows = displayHours(hoursArrayToMap(tenant.opening_hours ?? []));
  const hasHours = rows.some(([, h]) => h !== 'Fermé');
  const intro =
    `${tenant.name} vous accueille${tenant.city ? ` au cœur de ${tenant.city}` : ''}. ` +
    (tenant.short_pitch?.trim() || 'Commandez en ligne, retrait sur place ou livraison.');
  const mapQuery = encodeURIComponent(fullAddress || tenant.name);

  return (
    <section id="infos" className="mx-auto max-w-7xl px-4 py-16 lg:px-6 lg:py-24">
      <div className="grid items-start gap-10 lg:grid-cols-2 lg:gap-16">
        <div>
          <div
            className="mb-2 text-sm font-bold uppercase tracking-widest"
            style={{ color: 'var(--brand-primary)' }}
          >
            Nous trouver
          </div>
          <h2 className="display-font mb-6 text-4xl font-black lg:text-6xl">Le restaurant</h2>
          <p className="mb-8 text-lg leading-relaxed text-gray-700">{intro}</p>

          <div className="space-y-4">
            {fullAddress && <InfoLine icon="📍" label="Adresse" value={fullAddress} />}
            {tenant.phone1 && (
              <InfoLine icon="📞" label="Téléphone" value={tenant.phone1} link={`tel:${tenant.phone1}`} />
            )}
            <div className="pt-2">
              <div className="mb-2 text-sm font-bold text-gray-900">Retrouvez-nous partout</div>
              <Socials socials={tenant.socials} />
            </div>
          </div>

          {hasHours && (
            <div className="mt-8 rounded-2xl border border-gray-100 bg-gray-50 p-5">
              <div className="mb-3 flex items-center gap-2 font-bold">
                <span>🕒</span>
                <span>Horaires d'ouverture</span>
              </div>
              <div className="space-y-1.5 text-sm">
                {rows.map(([day, h]) => (
                  <div key={day} className="flex justify-between">
                    <span className="text-gray-600">{day}</span>
                    <span className={'font-semibold ' + (h === 'Fermé' ? 'text-red-500' : '')}>{h}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <button
            onClick={onOrder}
            className="mt-8 inline-flex items-center gap-2 rounded-2xl px-7 py-4 font-bold text-white shadow-lg transition active:scale-95"
            style={{ background: 'var(--brand-primary)' }}
          >
            Commander en ligne <span aria-hidden="true">→</span>
          </button>
        </div>

        {fullAddress && (
          <div className="relative h-[400px] overflow-hidden rounded-2xl border border-gray-200 bg-gray-100 shadow-xl lg:h-[560px]">
            <iframe
              src={`https://www.google.com/maps?q=${mapQuery}&output=embed`}
              className="h-full w-full"
              loading="lazy"
              title={`Plan ${tenant.name}`}
            />
            <a
              href={`https://www.google.com/maps/search/?api=1&query=${mapQuery}`}
              target="_blank"
              rel="noopener noreferrer"
              className="absolute bottom-4 left-4 right-4 flex items-center justify-between rounded-xl bg-white px-5 py-3 text-sm font-semibold shadow-lg hover:bg-gray-50"
            >
              <span>📍 Itinéraire Google Maps</span>
              <span aria-hidden="true">→</span>
            </a>
          </div>
        )}
      </div>
    </section>
  );
}
